/* global globalThis */
import axios from 'axios';

// Dev: prefer CRA proxy (/api → localhost:8000) unless REACT_APP_API_URL is set.
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? '/api' : 'http://localhost:8000/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {},
});

api.interceptors.request.use((config) => {
  const method = String(config.method || 'get').toLowerCase();
  const headers = config.headers || {};

  if (['post', 'put', 'patch'].includes(method) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const token = (sessionStorage.getItem('indicatorai_token') || localStorage.getItem('indicatorai_token'));
  if (token) {
    // eslint-disable-next-line no-param-reassign
    headers.Authorization = `Bearer ${token}`;
  }

  config.headers = headers;
  return config;
});

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = String(error.config?.url || '');
      const isAuthAttempt =
        url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/social');
      if (!isAuthAttempt) {
        localStorage.removeItem('indicatorai_token');
        sessionStorage.removeItem('indicatorai_token');
        globalThis.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  exchangeFirebaseToken: (idToken) => api.post('/auth/social', { provider: 'firebase', id_token: idToken }),
};

export const billingAPI = {
  getPlans: (config) => api.get('/billing/plans', config),
};

export const stocksAPI = {
  getValuationStats: () => api.get('/stocks/valuation/stats'),
  getStocks: (params) => api.get('/stocks', { params }),
  getStock: (ticker) => api.get(`/stocks/${ticker}`),
  getStockPrices: (ticker, days = 180) => api.get(`/stocks/${ticker}/prices`, { params: { days } }),
  getSectorStats: () => api.get('/sector-stats'),
  getRecommendations: () => api.get('/stocks/recommendations'),
  getTickerRecommendation: (ticker) => api.get(`/stocks/${ticker}/recommendation`),
};

export const indicatorsAPI = {
  getPillarScores: () => api.get('/indicators/pillars/latest'),
  getPillarWeights: () => api.get('/indicators/pillars/weights'),
  getIndicators: () => api.get('/indicators/'),
  updateIndicator: (indicatorId, data) => 
    api.put(`/indicators/${indicatorId}`, data),
  deleteIndicator: (indicatorId) => 
    api.delete(`/indicators/${indicatorId}`),
  updatePillarWeight: (pillarCode, data) => 
    api.put(`/indicators/pillars/weights/${pillarCode}`, data),
  deletePillarWeight: (pillarCode) => 
    api.delete(`/indicators/pillars/weights/${pillarCode}`),
};

export const valuationsAPI = {
  getSectorValuations: () => api.get('/sector-stats'),
  recalculate: () => api.post('/valuations/recalculate'),
  getAIInsights: (sectorName) => api.get(`/valuations/sectors/${encodeURIComponent(sectorName)}/ai-insights`),
  getTopSectorsAIInsights: (limit = 3) => api.get(`/valuations/ai-insights/top-sectors?limit=${limit}`),
  getAllAIInsights: () => api.get('/valuations/ai-insights/all'),
};

export const macroAPI = {
  getCurrent: () => api.get('/macro/current'),
  getHistory: (limit = 30) => api.get(`/macro/history?limit=${limit}`),
  getPillarScores: (date = null) => 
    api.get('/macro/pillars', { params: { calculation_date: date } }),
  getIndicatorScores: (date = null) => 
    api.get('/macro/indicators', { params: { calculation_date: date } }),
  getPillarTrends: (days = 90) => api.get(`/macro/pillar-trends?days=${days}`),
  recalculate: () => api.post('/macro/recalculate'),
  getConfig: () => api.get('/macro/config'),
};


export const forecastAPI = {
  getMarketForecast: (months = 6) => api.get(`/forecast/market?months=${months}`),
  getForecastDrivers: () => api.get('/forecast/drivers'),
  getCurrentState: () => api.get('/forecast/current-state'),
};

export const sectorsAPI = {
  getManagement: () => api.get('/sectors/management'),
  getDetails: (sectorCode) => api.get(`/sectors/${sectorCode}/details`),
  refresh: (sectorCode) => api.post(`/sectors/${sectorCode}/refresh`),
};

export const macroForecastAPI = {
  getTargets: () => api.get('/macro-forecast/targets'),
  getDashboard: () => api.get('/macro-forecast/dashboard'),
  getLatestForecast: (targetId, horizon = '1Y') => 
    api.get(`/macro-forecast/forecast/latest/${targetId}?horizon=${horizon}`),
  generateForecast: (data) => api.post('/macro-forecast/forecast/generate', data),
  generateAllForecasts: () => api.post('/macro-forecast/generate/all'),
  getDrivers: (forecastId) => api.get(`/macro-forecast/forecast/${forecastId}/drivers`),
  getBacktestMetrics: (targetId, horizon = '1Y') => 
    api.get(`/macro-forecast/backtest/${targetId}/metrics?horizon=${horizon}`),
  getDriftAlerts: (severity = null, limit = 10) => 
    api.get('/macro-forecast/drift/alerts', { params: { severity, limit } }),
  getIndicators: () => api.get('/macro-forecast/indicators'),
  getOutlookSummary: (horizon = '3M') => api.get('/macro-forecast/outlook-summary', { params: { horizon } }),
};

export const vnstockAPI = {
  // Stock prices
  getStockPrices: (ticker, startDate = null, endDate = null, limit = 100) => 
    api.get(`/vnstock/stocks/${ticker}/prices`, { params: { start_date: startDate, end_date: endDate, limit } }),
  getStockFundamentals: (ticker, limit = 10) => 
    api.get(`/vnstock/stocks/${ticker}/fundamentals`, { params: { limit } }),
  getAllStocksWithFundamentals: (limit = 50) => 
    api.get('/vnstock/stocks', { params: { limit } }),
  
  // Sectors
  getAllSectors: () => api.get('/vnstock/sectors'),
  getSectorsSummary: () => api.get('/vnstock/sectors/summary'),
  getSectorPrices: (sectorName, startDate = null, endDate = null, limit = 100) => 
    api.get(`/vnstock/sectors/${sectorName}/prices`, { params: { start_date: startDate, end_date: endDate, limit } }),
  getSectorReturns: (sectorName, limit = 100) => 
    api.get(`/vnstock/sectors/${sectorName}/returns`, { params: { limit } }),
  
  // Import logs
  getImportLogs: (dataType = null, limit = 20) => 
    api.get('/vnstock/import-logs', { params: { data_type: dataType, limit } }),
  
  // Statistics
  getOverviewStats: () => api.get('/vnstock/stats/overview'),
};

export const macroOutlookAPI = {
  // Level 2: Regime Classification
  getCurrentRegime: () => api.get('/macro-outlook/regime/current'),
  getRegimeHistory: (startDate, endDate) => 
    api.get('/macro-outlook/regime/history', { params: { start_date: startDate, end_date: endDate } }),
  
  // Level 3: News Shock
  getShockHistory: (days = 30) => 
    api.get('/macro-outlook/shock/history', { params: { days } }),
  getCurrentShock: () => api.get('/macro-outlook/shock/current'),
  
  // Level 4: Gating
  getCurrentGating: () => api.get('/macro-outlook/gating/current'),
  getGatingHistory: (days = 30) => 
    api.get('/macro-outlook/gating/history', { params: { days } }),
  
  // Combined dashboard data
  getDashboard: () => api.get('/macro-outlook/dashboard'),
  
  // Drivers (Explainability)
  getDrivers: (targetId, horizon) => 
    api.get('/macro-outlook/drivers', { params: { target_id: targetId, horizon } }),
  
  // Level 7: Sector Outlook
  getSectors: () => api.get('/macro-outlook/sectors'),
};

// Saturation Score API
export const saturationAPI = {
  getSummary: () => api.get('/saturation/summary'),
  getAllSectors: () => api.get('/saturation/sectors'),
  getSector: (sectorName) => api.get(`/saturation/sectors/${sectorName}`),
  getComponents: (sectorName) => api.get(`/saturation/components/${sectorName}`),
  getHeatmap: () => api.get('/saturation/heatmap'),
  getHistory: (sectorName, days = 30) => api.get(`/saturation/history/${sectorName}`, { params: { days } }),
};

// Macro Impulse Score API (Level 0 Engine)
export const macroImpulseAPI = {
  getLatest: () => api.get('/macro-impulse/latest'),
  getHistory: (sectorCode, days = 30) => api.get(`/macro-impulse/history/${sectorCode}`, { params: { days } }),
  getSummary: (days = 30) => api.get('/macro-impulse/summary', { params: { days } }),
  getDrivers: (sectorCode) => api.get(`/macro-impulse/drivers/${sectorCode}`),
  getMaturity: () => api.get('/macro-impulse/maturity'),
  getHotRuns: () => api.get('/macro-impulse/hot-runs'),
};

// Level 1 Analysis API (RS, Breadth, Enhanced Saturation)
export const level1API = {
  getLatest: () => api.get('/level1/latest'),
  getBreadth: (days = 30) => api.get('/level1/breadth', { params: { days } }),
  getRelativeStrength: (sector = null) => {
    const params = sector ? { sector } : {};
    return api.get('/level1/relative-strength', { params });
  },
  getTopOpportunities: (limit = 3) => api.get('/level1/top-opportunities', { params: { limit } }),
  getAvoidList: () => api.get('/level1/avoid-list'),
  calculate: () => api.post('/level1/calculate'),
};

// Downtrend Detection API
export const downtrendAPI = {
  // Detect downtrend for a specific stock
  detect: (symbol, startDate, endDate, lookbackDays = 30) => 
    api.post('/downtrend/detect', {
      symbol,
      start_date: startDate,
      end_date: endDate,
      lookback_days: lookbackDays
    }),
  
  // Batch detect downtrends for multiple stocks
  batchDetect: (stocks, startDate, endDate, lookbackDays = 30) => 
    api.post('/downtrend/batch-detect', {
      stocks,
      start_date: startDate,
      end_date: endDate,
      lookback_days: lookbackDays
    }),
  
  // Get macro indicators configuration
  getIndicators: () => api.get('/downtrend/indicators'),
  
  // Get active downtrend alerts
  getAlerts: () => api.get('/downtrend/alerts'),
  
  // Get downtrend history for a stock
  getHistory: (symbol, days = 90) => 
    api.get(`/downtrend/history/${symbol}`, { params: { days } }),
};

// Alerts API
export const alertsAPI = {
  getRecent: (params = {}) => api.get('/alerts/recent', { params }),
  getRecommendations: (style, params = {}) => api.get(`/alerts/recommendations/${style}`, { params }),
  getBreakouts: (params = {}) => api.get('/alerts/breakouts', { params }),
  getRegime: (params = {}) => api.get('/alerts/regime', { params }),
  getRisk: (params = {}) => api.get('/alerts/risk', { params }),
  getSummary: (days = 7) => api.get('/alerts/summary', { params: { days } }),
  getHealth: () => api.get('/alerts/health'),
};

// AI Recommendations API (New Authenticated Endpoints)
export const aiRecommendationsAPI = {
  getAllRecommendations: (limit = 20) => api.get('/stocks/recommendations', { params: { limit } }),
  getAIRecommendationForTicker: (ticker) => 
    api.get(`/ai/recommendations/${ticker}`),
  getBatchAIRecommendations: (tickers, style = 'VALUE') => 
    api.post('/ai/recommendations/batch', { tickers, style }),
  getAIConfig: () => api.get('/ai/config'),
  getAIHealth: () => api.get('/ai/health'),
};

// Observations API
export const observationsAPI = {
  // Get latest observation per indicator
  getLatest: () => api.get('/observations/latest'),
  
  // Get observations with filters
  getAll: (params = {}) => api.get('/observations/', { params }),
  
  // Get history for charts
  getHistory: (startDate = null, limit = 5000) => 
    api.get('/observations/', { params: { start_date: startDate, limit } }),
  
  // CRUD operations
  create: (data) => api.post('/observations/', data),
  update: (date, indicatorId, data) => 
    api.put(`/observations/${date}/${indicatorId}`, data),
  delete: (date, indicatorId) => 
    api.delete(`/observations/${date}/${indicatorId}`),
};

// Indicators API (extended)
export const indicatorsFullAPI = {
  ...indicatorsAPI,
  getAll: () => api.get('/indicators/'),
};

// Extraction Review API
export const extractionReviewAPI = {
  getPendingProposals: (limit = 100) => 
    api.get('/extraction-review/proposals', { params: { status: 'pending', limit } }),
  
  reviewProposals: (proposalIds, action, reviewedBy, notes = '') => 
    api.post('/extraction-review/review', {
      proposal_ids: proposalIds,
      action,
      reviewed_by: reviewedBy,
      notes,
    }),
};

// Risk Management API
export const riskAPI = {
  // Get daily risk signals
  getDailySignals: (calcDate = null) => {
    const params = calcDate ? { calc_date: calcDate } : {};
    return api.get('/risk/signals/daily', { params });
  },
  
  // Check recovery conditions
  checkRecovery: (calcDate = null, lookbackDays = 5) => {
    const params = { lookback_days: lookbackDays };
    if (calcDate) params.calc_date = calcDate;
    return api.get('/risk/recovery-check', { params });
  },
  
  // Check exit conditions
  checkExit: (data) => api.post('/risk/check-exit', data),
  
  // Calculate trailing stop
  calculateTrailingStop: (data) => api.post('/risk/trailing-stop', data),
  
  // Check diversification limits
  checkDiversification: (data) => api.post('/risk/check-diversification', data),
  
  // Calculate position size
  calculatePositionSize: (data) => api.post('/risk/position-size', data),
  
  // Run backtest
  runBacktest: (startDate, endDate, slope = 1.4, maxDdLimit = -0.1) => 
    api.get('/risk/backtest', { 
      params: { 
        start_date: startDate, 
        end_date: endDate,
        slope,
        max_dd_limit: maxDdLimit
      } 
    }),
  
  // Validate entry conditions
  validateEntry: (data) => api.get('/risk/validate-entry', { params: data }),
  
  // Get portfolio equity history
  getEquityHistory: (days = 30) => 
    api.get('/risk/equity-history', { params: { days } }),
};

export const tradingDecisionAPI = {
  getRegime: (calcDate = null) => 
    api.get('/trading-decision/regime', { params: calcDate ? { calc_date: calcDate } : {} }),
  getTradeIdeas: (calcDate = null, nav = 1000000000, maxIdeas = 20, useCache = true) => 
    api.get('/trading-decision/trade-ideas', { 
      params: { 
        calc_date: calcDate,
        nav,
        max_ideas: maxIdeas,
        use_cache: useCache
      } 
    }),
  getDecision: (calcDate = null, nav = 1000000000) => 
    api.get('/trading-decision/decision', { 
      params: { 
        calc_date: calcDate,
        nav 
      } 
    }),
  getSectorRankings: (calcDate = null) => 
    api.get('/trading-decision/sector-rankings', { 
      params: { calc_date: calcDate } 
    }),
};

export const scannerAPI = {
  // Get market status for filtering
  getMarketStatus: () => api.get('/scanner/market-status'),
  
  // Run bullish scan with filters
  scanBullish: (params) => api.post('/scanner/bullish', params),
  
  // Get pattern details for a specific ticker
  getPatternDetails: (ticker) => api.get(`/scanner/patterns/${ticker}`),
  
  // Get pattern performance metrics (win-rates, badges)
  getPatternPerformance: () => api.get('/scanner/patterns/performance-metrics'),
  
  // Force refresh pattern performance cache
  refreshPatternPerformance: () => api.post('/scanner/patterns/performance-metrics/refresh'),

  // Early Pivot scan (setup/trigger 10/20)
  scanEarlyPivot: (params = {}) => api.post('/scanner/early-pivot', params),

  // Scan history
  getHistorySessions: (limit = 30) => api.get('/scanner/history/sessions', { params: { limit } }),
  getHistorySession: (sessionId) => api.get(`/scanner/history/sessions/${sessionId}`),
  getTickerHistory: (ticker, limit = 20) => api.get(`/scanner/history/ticker/${ticker}`, { params: { limit } }),
  getPatternAccuracy: () => api.get('/scanner/history/patterns'),
  getHistorySummary: () => api.get('/scanner/history/summary'),
};

export const monitoringAPI = {
  // Get scanner signal tracking stats (7-day default)
  getScannerStats: (days = 7) => api.get('/monitoring/scanner-stats', { params: { days } }),
  
  // Get pattern performance metrics from backtests
  getPatternPerformance: () => api.get('/monitoring/pattern-performance'),
  
  // Get scanner system health status
  getScannerHealth: () => api.get('/monitoring/scanner-health'),
};

export const marketCorrectionAPI = {
  // New Market Correction Prediction System
  getCurrent: () => api.get('/market-correction/current'),
  getHistory: (lookbackDays = 30) => api.get('/market-correction/history', { params: { lookback_days: lookbackDays } }),
  getFactors: (date = null) => api.get('/market-correction/factors', { params: date ? { date } : {} }),
  getAlert: () => api.get('/market-correction/alert'),
  getEntrySignal: () => api.get('/market-correction/entry-signal'),
  recalculate: () => api.post('/market-correction/recalculate'),
  triggerCalculation: (includeBreadth = true) => api.post('/market-correction/calculate', {}, { params: { include_breadth: includeBreadth } }),
  
  // Legacy endpoints (market correction probability)
  getLatest: (horizonDays = 10) => api.get('/market/correction/latest', { params: { h: horizonDays } }),
  getHistoryLegacy: (startDate, endDate, horizonDays = 10) => api.get('/market/correction/history', { params: { start: startDate, end: endDate, h: horizonDays } }),
  getMetrics: (modelVersion = null) => api.get('/market/correction/metrics', { params: modelVersion ? { model_version: modelVersion } : {} }),
  getExplanation: (topN = 8) => api.get('/market/correction/explain', { params: { top_n: topN } }),

  // Multi-horizon endpoints
  getMultiHorizon: () => api.get('/market/correction/multi-horizon'),
  getMultiHorizonLatest: () => api.get('/market/correction/multi-horizon/latest'),

  // Accuracy tracking
  getAccuracy: (modelVersion = null) => api.get('/market/correction/accuracy', { params: modelVersion ? { model_version: modelVersion } : {} }),
  backfillPredictions: (startDate = null, endDate = null) => api.post('/market/correction/backfill-predictions', {}, { params: { ...(startDate && { start_date: startDate }), ...(endDate && { end_date: endDate }) } }),
};

export const downshiftScannerAPI = {
  // Old API: Scan for downshift/weakness signals
  scan: (params) => api.post('/scanner/downshift', params),
  
  // NEW API: Regime-aware scanner (3 categories)
  scanRegime: (params) => api.post('/scanner/downshift-regime', params),
};

// Smart Entry Scanner
export const easyStepAPI = {
  scan: (params = {}) => api.post('/scanner/easy-step', params),
};

// Smart Entry Tracker
export const smartEntryTrackerAPI = {
  getRecommendations: (params = {}) => api.get('/scanner/smart-entry/recommendations', { params }),
  getStats: () => api.get('/scanner/smart-entry/stats'),
  recordNow: () => api.post('/scanner/smart-entry/record'),
};

// Money Flow Scanner
export const moneyFlowAPI = {
  scan: (params = {}) => api.post('/scanner/money-flow', params),
  getSummary: () => api.get('/scanner/money-flow/summary'),
  getTicker: (ticker, params = {}) => api.get(`/scanner/money-flow/ticker/${ticker}`, { params }),
  getRecommendations: (params = {}) => api.post('/scanner/recommendations', params),
};

// Distribution Detector
export const distributionAPI = {
  getLeaderboard: (params = {}) => api.get('/distribution/leaderboard', { params }),
  getSummary: (params = {}) => api.get('/distribution/summary', { params }),
  getTicker: (ticker, limit = 30) => api.get(`/distribution/ticker/${ticker}`, { params: { limit } }),
  calculate: (params = {}) => api.post('/distribution/calculate', null, { params }),
  getMarket: (params = {}) => api.get('/distribution/market', { params }),
  // Sector Regime
  getSectorRegime: (params = {}) => api.get('/distribution/sector-regime', { params }),
  getSectorRegimeSummary: (params = {}) => api.get('/distribution/sector-regime/summary', { params }),
  getSectorHistory: (sector, limit = 30) => api.get(`/distribution/sector-regime/history/${sector}`, { params: { limit } }),
  getSectorMembers: (sector, params = {}) => api.get(`/distribution/sector-regime/members/${sector}`, { params }),
  getTickerSectorContext: (ticker, params = {}) => api.get(`/distribution/sector-regime/ticker/${ticker}`, { params }),
  calculateSectorRegime: (params = {}) => api.post('/distribution/sector-regime/calculate', null, { params }),
};

// Silent Accumulation Scanner
export const silentAccumulationAPI = {
  scan: (params = {}) => api.post('/scanner/silent-accumulation', params),
  getLeaderboard: (params = {}) => api.get('/scanner/silent-accumulation/leaderboard', { params }),
  getStats: (style = 'swing') => api.get('/scanner/silent-accumulation/stats', { params: { style } }),
  getTicker: (ticker, params = {}) => api.get(`/scanner/silent-accumulation/ticker/${ticker}`, { params }),
  getTopPicks: (params = {}) => api.get('/scanner/silent-accumulation/top-picks', { params }),
};

// VPA (Volume-Price Analysis) Scanner
export const vpaAPI = {
  scan: (params = {}) => api.post('/scanner/vpa', params),
  getLeaderboard: (params = {}) => api.get('/scanner/vpa/leaderboard', { params }),
  getSummary: (params = {}) => api.get('/scanner/vpa/summary', { params }),
  getTicker: (ticker, days = 60) => api.get(`/scanner/vpa/ticker/${ticker}`, { params: { days } }),
  getDates: (days = 60) => api.get('/scanner/vpa/dates', { params: { days } }),
  getSignalHistory: (days = 30, action = null) =>
    api.get('/scanner/vpa/history/signals', { params: { days, ...(action ? { action } : {}) } }),
};

// Market Divergence Scanner
export const marketDivergenceAPI = {
  scan: (params = {}) => api.post('/scanner/market-divergence', params),
};

// Early Divergence Watchlist
export const earlyDivergenceAPI = {
  scan: (params = {}) => api.post('/scanner/early-divergence-watchlist', params),
};

// Market Resilience Scanner
export const marketResilienceAPI = {
  scan: (params = {}) => api.post('/scanner/market-resilience', params),
  getLatest: () => api.get('/scanner/market-resilience/latest'),
  getByTicker: (ticker) => api.get(`/scanner/market-resilience/ticker/${ticker}`),
};

// Smart Money Unified Score
export const smartMoneyUnifiedAPI = {
  scan: (params = {}) => api.post('/scanner/smart-money-unified', params),
  getTop: (params = {}) => api.get('/scanner/smart-money-unified/top', { params }),
  getTickerHistory: (ticker, params = {}) => api.get(`/scanner/smart-money-unified/ticker/${ticker}`, { params }),
};

// Duplicate Signature (Cross-Engine Consensus)
export const duplicateSignatureAPI = {
  scan: (params = {}) => api.post('/scanner/duplicate-signature', params),
};

export const backtestValidationAPI = {
  getResults: () => api.get('/scanner/backtest-validation'),
};

export const marketInternalRadarAPI = {
  getSummary: (tradeDate = null) => api.get('/market-internal-radar/summary', { params: tradeDate ? { trade_date: tradeDate } : {} }),
  getCandidates: (params = {}) => api.get('/market-internal-radar/candidates', { params }),
  getSectors: (tradeDate = null, limit = 50) => api.get('/market-internal-radar/sectors', { params: { ...(tradeDate ? { trade_date: tradeDate } : {}), limit } }),
  getHistory: (freq = 'D', limit = 60) => api.get('/market-internal-radar/history', { params: { freq, limit } }),
  refresh: (payload = {}) => api.post('/market-internal-radar/refresh', payload),
};

export const marketContextAPI = {
  get: () => api.get('/scanner/market-context'),
};

export const commandCenterAPI = {
  get: () => api.get('/scanner/command-center'),
};

export const performanceAPI = {
  getMetrics: (days = 30, signalType = null) => 
    api.get('/performance/metrics', { params: { days, signal_type: signalType } }),
  getRecentSignals: (days = 30, limit = 50) => 
    api.get('/performance/signals/recent', { params: { days, limit } }),
  getDashboard: () => 
    api.get('/performance/dashboard'),
  recordSignal: (data) => 
    api.post('/performance/signals/record', data),
  updateOutcomes: (ticker, signalDate, signalType) => 
    api.post('/performance/signals/update-outcomes', { ticker, signal_date: signalDate, signal_type: signalType }),
  triggerScanner: () => api.post('/performance/trigger-scanner'),
};

export const breakoutAPI = {
  getMetadata: (params = {}) => api.get('/breakout-candidates/metadata', { params }),
  getCandidates: (params = {}) => api.get('/breakout-candidates', { params }),
  getHistory: (params = {}) => api.get('/breakout-candidates/history', { params }),
  getTickerHistory: (ticker, params = {}) => api.get(`/breakout-candidates/ticker/${ticker}/history`, { params }),
  triggerRefresh: (data = {}) => api.post('/breakout-candidates/refresh', data),
  getRefreshStatus: (taskId) => api.get(`/breakout-candidates/tasks/${taskId}`),
};

export const morningBriefAPI = {
  get: () => api.get('/morning-brief'),
};

export const earlyAccumulationAPI = {
  getCandidates: (params = {}) => api.get('/early-accumulation/candidates', { params }),
  getHistory: (params = {}) => api.get('/early-accumulation/history', { params }),
  getMonitorSummary: (params = {}) => api.get('/early-accumulation/monitor-summary', { params }),
};

// Portfolio Management API
export const portfolioAPI = {
  // Portfolio Health Score
  calculateHealth: (portfolio, lookbackDays = 60, benchmark = 'VNINDEX') => 
    api.post('/portfolio/health', {
      portfolio: {
        name: portfolio.name || 'My Portfolio',
        holdings: portfolio.holdings,
        benchmark: benchmark
      },

      lookback_days: lookbackDays
    }),
  
  // Portfolio Health Actions
  getActions: (portfolio, lookbackDays = 60, currentDrawdown = 0, currentNav = 1000000000) =>
    api.post('/portfolio/health/actions', {
      portfolio: {
        name: portfolio.name || 'My Portfolio',
        holdings: portfolio.holdings
      },
      lookback_days: lookbackDays,
      current_drawdown: currentDrawdown,
      current_nav: currentNav
    }),
  
  quickHealthCheck: (holdings) => 
    api.post('/portfolio/health/quick', { holdings }),
  
  // Portfolio Construction
  getRegimeInfo: () => 
    api.get('/portfolio/regimes'),
  
  // Safe Invest - Market Regime Engine
  getSafeInvestRegime: () =>
    api.get('/safe-invest/market-regime'),
  
  getSafeInvestSectorRotation: () =>
    api.get('/safe-invest/sector-rotation'),
  
  getSafeInvestRegimeHistory: (days = 30) =>
    api.get(`/safe-invest/regime-history?days=${days}`),
  
  getSafeInvestRegimeStats: (days = 90) =>
    api.get(`/safe-invest/regime-stats?days=${days}`),
  
  getSafeInvestRegimesInfo: () =>
    api.get('/safe-invest/regimes/info'),
  
  // Regime Watchlist Builder
  getRegimeWatchlist: (params = {}) =>
    api.get('/safe-invest/regime-watchlist', { params }),

  // Dynamic Backtest Stats (B1)
  getRegimeBacktestStats: (regime = null) =>
    api.get('/safe-invest/regime-watchlist/backtest-stats', { params: regime ? { regime } : {} }),

  // Historical Watchlist Performance (B3)
  getWatchlistHistoryPerformance: (days = 30) =>
    api.get('/safe-invest/regime-watchlist/history-performance', { params: { days } }),

  recommendPortfolio: (params = {}) => 
    api.get('/portfolio/recommend', { 
      params: {
        top_sectors: params.topSectors || 4,
        stocks_per_sector: params.stocksPerSector || 3,
        cap_single: params.capSingle || 0.15,
        cap_sector: params.capSector || 0.4,
        lookback_days: params.lookbackDays || 60,
        // Dynamic thresholds (Phase 1)
        stretch_threshold: params.stretchThreshold,
        atr_z_threshold: params.atrZThreshold,
        rsi_threshold: params.rsiThreshold,
        entry_score_min: params.entryScoreMin,
        mdd_threshold: params.mddThreshold
      }
    }),
  
  runBacktest: (params = {}) =>
    api.post('/portfolio/backtest', null, {
      params: {
        start_date: params.startDate,
        end_date: params.endDate,
        initial_capital: params.initialCapital || 1000000000,
        rebalance_frequency: params.rebalanceFrequency || 'monthly',
        // Portfolio construction parameters
        top_sectors: params.topSectors || 4,
        stocks_per_sector: params.stocksPerSector || 3,
        cap_single: params.capSingle || 0.15,
        cap_sector: params.capSector || 0.4,
        lookback_days: params.lookbackDays || 60
      }
    }),
  
  getPortfolioSummary: (params = {}) => 
    api.get('/portfolio/recommend/summary', { params }),
  
  // Market Breadth & Risk Control (Sprint 1-4)
  getMarketBreadth: (lookbackDays = 60) =>
    api.get('/market/breadth', { params: { lookback_days: lookbackDays } }),
  
  getMarketExposure: () =>
    api.get('/market/exposure'),
  
  getSectorRotation: (lookbackDays = 60) =>
    api.get('/sector/rotation', { params: { lookback_days: lookbackDays } }),
  
  getLeadingSectors: (lookbackDays = 60) =>
    api.get('/sector/rotation/leading', { params: { lookback_days: lookbackDays } }),
  
  getKillSwitchStatus: () =>
    api.get('/kill-switch/status'),
  
  getKillSwitchTriggers: () =>
    api.get('/kill-switch/triggers'),
  
  // Unified Recommendation Engine (NEW)
  getRecommendations: (params = {}) => api.get('/portfolio/recommend', { params }),
  getAlerts: (params = {}) => api.get('/portfolio/alerts', { params }),
  getMarketRegime: (params = {}) => api.get('/portfolio/market', { params }),
  getSectorScores: (params = {}) => api.get('/portfolio/sectors', { params }),
  getWatchlist: (params = {}) => api.get('/portfolio/watchlist', { params }),
};

// Risk-Opportunity Matrix API
export const riskOpportunityAPI = {
  // Market Risk
  getMarketRisk: () => api.get('/v1/risk-opportunity/market-risk/latest'),
  
  // Stocks by Quadrant
  getStocksByQuadrant: (quadrant, limit = 50, asOfDate = null, minAvgValue = null) => 
    api.get(`/v1/risk-opportunity/stocks/quadrant/${quadrant}`, { 
      params: { limit, ...(asOfDate && { as_of_date: asOfDate }), ...(minAvgValue != null && { min_avg_value: minAvgValue }) } 
    }),
  
  // Safe Opportunities
  getSafeOpportunities: (limit = 20, asOfDate = null, minAvgValue = null) => 
    api.get('/v1/risk-opportunity/stocks/safe-opportunities', { 
      params: { limit, ...(asOfDate && { as_of_date: asOfDate }), ...(minAvgValue != null && { min_avg_value: minAvgValue }) } 
    }),
  
  // Backtesting
  listStrategies: () => api.get('/v1/risk-opportunity/backtest/strategies'),
  
  runBacktest: (data) => api.post('/v1/risk-opportunity/backtest/run', data),
  
  compareStrategies: (startDate, endDate, initialCapital = 100000000) => 
    api.get('/v1/risk-opportunity/backtest/compare', { 
      params: { start_date: startDate, end_date: endDate, initial_capital: initialCapital } 
    }),
  
  // Signal Accuracy
  getSignalAccuracy: (lookbackDays = 90, quadrant = null) =>
    api.get('/v1/risk-opportunity/signal-accuracy', {
      params: { lookback_days: lookbackDays, ...(quadrant && { quadrant }) }
    }),

  // Score History for a stock
  getStockScoreHistory: (ticker, days = 60) =>
    api.get(`/v1/risk-opportunity/stocks/${ticker}/history`, { params: { days } }),

  // Quadrant Changes
  getQuadrantChanges: (asOfDate = null, days = 1) =>
    api.get('/v1/risk-opportunity/quadrant-changes', {
      params: { ...(asOfDate && { as_of_date: asOfDate }), days }
    }),

  // Market Risk History
  getMarketRiskHistory: (days = 30) =>
    api.get('/v1/risk-opportunity/market-risk/history', { params: { days } }),

  // Available Dates for date picker
  getAvailableDates: () => api.get('/v1/risk-opportunity/available-dates'),
};

// High-Vol Regime Detector API
export const highVolRegimeAPI = {
  // Get latest regime for all indexes or specific index
  getLatest: (indexCode = null) => 
    api.get('/high-vol/latest', { 
      params: indexCode ? { index_code: indexCode } : {} 
    }),
  
  // Get historical regime data for specific index
  getHistory: (indexCode, days = 30) =>
    api.get('/high-vol/history', { 
      params: { index_code: indexCode, days } 
    }),
  
  // Get summary across all indexes (overall risk, breadth avg, high_vol count)
  getSummary: () =>
    api.get('/high-vol/summary'),
  
  // Get VN30F derivatives history (basis, OI, risk signal)
  getVN30Derivatives: (days = 30) =>
    api.get('/high-vol/derivatives/vn30', { 
      params: { days } 
    }),
  
  // Trigger manual recalculation of High-Vol regime
  recalculate: () =>
    api.post('/high-vol/recalculate'),
};

// Reversal Catcher API - Bottom-fishing strategy with Capitulation/Snapback detection
export const reversalCatcherAPI = {
  // Scan stocks for reversal signals
  scan: (params = {}) => api.post('/reversal-catcher/scan', {
    tickers: params.tickers || null,
    snap_mode: params.snap_mode || 'conservative',
    save_to_db: params.save_to_db !== false,
  }),
  
  // Get signals for specific ticker
  getSignals: (ticker, params = {}) => api.get(`/reversal-catcher/signals/${ticker}`, {
    params: {
      start_date: params.start_date,
      end_date: params.end_date,
      limit: params.limit || 10,
    },
  }),
  
  // Get capitulation stocks (watch for snapback)
  getCapitulation: (params = {}) => api.get('/reversal-catcher/capitulation', {
    params: {
      limit: params.limit || 20,
      min_cap_score: params.min_cap_score || 2,
    },
  }),
  
  // Get snapback opportunities (ready to buy)
  getSnapback: (params = {}) => api.get('/reversal-catcher/snapback', {
    params: {
      limit: params.limit || 20,
      states: params.states || ['PROBE_SNAP', 'ADD_SNAP'],
    },
  }),
  
  // Get watchlist (capitulation candidates not yet triggered)
  getWatchlist: (params = {}) => api.get('/reversal-catcher/watchlist', {
    params: {
      limit: params.limit || 100,
      min_cap_score: params.min_cap_score || 1,
    },
  }),
  
  // Get performance metrics
  getPerformance: (params = {}) => api.get('/reversal-catcher/performance', {
    params: {
      start_date: params.start_date,
      end_date: params.end_date,
      days: params.days || 30,
    },
  }),
  
  // Run backtest
  runBacktest: (params = {}) => api.post('/reversal-catcher/backtest', {
    start_date: params.start_date,
    end_date: params.end_date,
    tickers: params.tickers || null,
    initial_capital: params.initial_capital || 100000000,
    max_positions: params.max_positions || 5,
    snap_mode: params.snap_mode || 'conservative',
    min_c_score: params.min_c_score || 1,
    only_capitulation: params.only_capitulation || false,
    use_settlement: params.use_settlement !== false,
  }),
  
  // Get backtest history
  getBacktestHistory: (limit = 50) => api.get('/reversal-catcher/backtest/history', {
    params: { limit },
  }),
  
  // Get specific backtest run details
  getBacktestRun: (runId) => api.get(`/reversal-catcher/backtest/${runId}`),
};

// Calibration API
export const calibrationAPI = {
  // Get calibration stats and trade summary
  getStats: (strategy = 'scanner_patterns') => 
    api.get('/calibration/stats', { params: { strategy } }),
  
  // Get calibration health and accuracy
  getHealth: (strategy = 'scanner_patterns') =>
    api.get('/calibration/health', { params: { strategy } }),
  
  // Get current calibration data
  getCurrent: (strategy = 'scanner_patterns') =>
    api.get('/calibration/current', { params: { strategy } }),
  
  // Update calibration from backtest results
  update: (strategy, params = {}) =>
    api.post('/calibration/update', {
      min_trades_per_bucket: params.min_trades_per_bucket || 10,
      lookback_days: params.lookback_days || 365,
      learning_rate: params.learning_rate || 0.3,
    }, { params: { strategy } }),
  
  // Log a trade result
  logTrade: (trade, strategy = 'scanner_patterns') =>
    api.post('/calibration/trades', trade, { params: { strategy } }),
  
  // Update trade exit
  updateExit: (tradeId, exitData) =>
    api.patch(`/calibration/trades/${tradeId}/exit`, exitData),
  
  // Get pattern analysis
  getPatternAnalysis: (strategy = 'scanner_patterns', minTrades = 20) =>
    api.get('/calibration/pattern-analysis', { 
      params: { strategy, min_trades: minTrades } 
    }),
  
  // Get recent trade results list
  getTradesList: (strategy = 'scanner_patterns', limit = 50) =>
    api.get('/calibration/trades-list', { params: { strategy, limit } }),

  // Reset calibration
  reset: (strategy = 'scanner_patterns') =>
    api.post('/calibration/reset', {}, { params: { strategy } }),
};

export const rotationAPI = {
  getSummary: () => api.get('/v2/rotation/summary'),
  getMetrics: (params) => api.get('/v2/rotation/metrics', { params }),
  getActiveSectors: (params) => api.get('/v2/rotation/sectors/active', { params }),
  getStockCandidates: (params) => api.get('/v2/rotation/stocks/candidates', { params }),
  getHoldings: (params) => api.get('/v2/rotation/holdings', { params }),
  getAlerts: (params) => api.get('/v2/rotation/alerts', { params }),
  getHistory: (params) => api.get('/v2/rotation/history', { params }),
  getMoneyFlow: (days = 7) => api.get('/v2/rotation/money-flow', { params: { days } }),
  
  // Entry Timing System (Phase 2)
  getEntryTiming: (params) => api.get('/v2/rotation/entry-timing', { params }),
  getStockEntryTiming: (stockCode, params) => api.get(`/v2/rotation/entry-timing/${stockCode}`, { params }),
  getWatchlist: (params) => api.get('/v2/rotation/watchlist', { params }),
  getOutsideWatchlist: (params) => api.get('/v2/rotation/outside-watchlist', { params }),
};

// Market Engine API - Stock Candidates with Unified Scoring
export const marketEngineAPI = {
  getCandidates: (params = {}) => api.get('/market/candidates', { 
    params: {
      calc_date: params.calcDate,
      nav: params.nav || 1000000000,
      risk_per_trade: params.riskPerTrade || 0.01,
      topk: params.topk || 20,
      min_liquidity: params.minLiquidity || 10000000000,
      min_price: params.minPrice || 10000,
    }
  }),
  
  getStatus: (calcDate = null) => api.get('/market/engine/status', {
    params: calcDate ? { calc_date: calcDate } : {}
  }),
};

// T+ Trading API - Signals & Manual Position Tracking
export const tplusAPI = {
  scan: (params = {}) => api.get('/tplus/scan', { params }),
  getPositions: () => api.get('/tplus/positions'),
  getExits: () => api.get('/tplus/exits'),
  getPerformance: () => api.get('/tplus/performance'),
  createPosition: (data) => api.post('/tplus/position', data),
  executeExit: (data) => api.post('/tplus/exit', data),
  getWatchlist: (params = {}) => api.get('/tplus/watchlist', { params }),
  getSignalHistory: (params = {}) => api.get('/tplus/signals/history', { params }),
};

// T+ Paper Trading API - Auto Entry/Exit for Reference
export const tplusPaperAPI = {
  getPositions: (status = null) => 
    api.get('/tplus-paper/positions', { params: status ? { status } : {} }),
  getPerformance: () => 
    api.get('/tplus-paper/performance'),
};

// Market Recovery Detection API - VN30 + VN30F1M Recovery Signal Analysis
export const marketRecoveryAPI = {
  // Get latest recovery signal
  getLatest: () => 
    api.get('/market-recovery/latest'),
  
  // Get historical signals
  getHistory: (limit = 30) => 
    api.get('/market-recovery/history', { params: { limit } }),
  
  // Get statistics for a time period
  getStats: (days = 30) => 
    api.get('/market-recovery/stats', { params: { days } }),
  
  // Get signal for specific date
  getSignalByDate: (date) => 
    api.get(`/market-recovery/date/${date}`),
  
  // Get signals breakdown (count by phase/action)
  getSignalsBreakdown: (days = 30) => 
    api.get('/market-recovery/signals-breakdown', { params: { days } }),
  
  // Trigger manual calculation
  calculate: (data = {}) => 
    api.post('/market-recovery/calculate', data),
};

// Hedge Detection API - Short hedge signal analysis using VN30 + VN30F1M
export const hedgeDetectionAPI = {
  // Get latest hedge signal
  getLatest: () => 
    api.get('/hedge-detection/latest'),
  
  // Get historical signals
  getHistory: (limit = 30) => 
    api.get('/hedge-detection/history', { params: { limit } }),
  
  // Get statistics for a time period
  getStats: (days = 30) => 
    api.get('/hedge-detection/stats', { params: { days } }),
  
  // Get signal for specific date
  getSignalByDate: (date) => 
    api.get(`/hedge-detection/date/${date}`),
  
  // Get signals breakdown (count by zone/action)
  getSignalsBreakdown: (days = 30) => 
    api.get('/hedge-detection/signals-breakdown', { params: { days } }),
  
  // Trigger manual calculation
  calculate: (data = {}) => 
    api.post('/hedge-detection/calculate', data),
};

export const automationAPI = {
  getDashboard: () => api.get('/automation/dashboard'),
  triggerJob: (jobKey) => api.post(`/automation/jobs/${jobKey}/trigger`),
  triggerFlow: (flowKey) => api.post(`/automation/flows/${flowKey}/trigger`),
};

// Market Sentiment API - MSI (Market Sentiment Index) with Fear/Greed components
export const sentimentAPI = {
  // Get latest sentiment signal
  getLatest: () => 
    api.get('/sentiment/latest').then(res => res.data),
  
  // Get historical signals
  getHistory: (params = {}) => 
    api.get('/sentiment/history', { params }).then(res => res.data),
  
  // Get statistics for a time period
  getStats: (params = {}) => 
    api.get('/sentiment/stats', { params }).then(res => res.data),
  
  // Get signal for specific date
  getSignalByDate: (date) => 
    api.get(`/sentiment/by-date/${date}`).then(res => res.data),
  
  // Get signals breakdown
  getSignalsBreakdown: (days = 30) => 
    api.get('/sentiment/signals-breakdown', { params: { days } }),
  
  // Trigger manual calculation
  calculate: (data = {}) => 
    api.post('/sentiment/calculate', data),
};

// Market Psychology API (EXPERIMENTAL) - 8-state model, see backend/services/psychology/
export const psychologyAPI = {
  // Get latest signal for an index (default VN30)
  getLatest: (indexCode = 'VN30') =>
    api.get('/psychology/latest', { params: { index_code: indexCode } }).then(res => res.data),

  // Get historical signals
  getHistory: (params = {}) =>
    api.get('/psychology/history', { params }).then(res => res.data),

  // Get statistics for a time period
  getStats: (params = {}) =>
    api.get('/psychology/stats', { params }).then(res => res.data),

  // Get signal for specific date
  getSignalByDate: (date, indexCode = 'VN30') =>
    api.get(`/psychology/by-date/${date}`, { params: { index_code: indexCode } }).then(res => res.data),

  // Trigger manual calculation
  calculate: (data = {}) =>
    api.post('/psychology/calculate', data).then(res => res.data),
};

export const phaseAPI = {
  getSummary: (tradeDate = null) =>
    api.get('/phase/summary', { params: { trade_date: tradeDate } }),
  getIndices: ({ tradeDate = null, minConfidence = 0 } = {}) =>
    api.get('/phase/indices', { params: { trade_date: tradeDate, min_confidence: minConfidence } }),
  scan: (phase, { tradeDate = null, minConfidence = 0, limit = 50 } = {}) =>
    api.get('/phase/scan', { params: { phase, trade_date: tradeDate, min_confidence: minConfidence, limit } }),
  getPhase: (ticker) =>
    api.get(`/phase/${ticker}`),
  getPhaseLive: (ticker, asOfDate = null) =>
    api.get(`/phase/${ticker}/live`, { params: { as_of_date: asOfDate } }),
  getHistory: (ticker, days = 90) =>
    api.get(`/phase/${ticker}/history`, { params: { days } }),
  refresh: () =>
    api.post('/phase/refresh'),
  refreshStatus: () =>
    api.get('/phase/refresh/status'),
  getPreMarkup: (minReadiness = 0.3, limit = 30) =>
    api.get('/phase/pre-markup', { params: { min_readiness: minReadiness, limit } }),
};

// ── Legendary Strategies API ──
export const legendaryAPI = {
  // List all 5 strategies with metadata
  getStrategies: () =>
    api.get('/legendary/strategies'),
  // Scan universe with all or selected strategies
  scan: ({ strategies = null, minScore = 0, lookbackDays = 300 } = {}) =>
    api.get('/legendary/scan', { params: { strategies, min_score: minScore, lookback_days: lookbackDays } }),
  // Scan universe with one strategy
  scanOne: (strategy, { minScore = 0, lookbackDays = 300 } = {}) =>
    api.get(`/legendary/scan/${strategy}`, { params: { min_score: minScore, lookback_days: lookbackDays } }),
  // Run all 5 strategies on one stock
  analyzeStock: (ticker) =>
    api.get(`/legendary/stock/${ticker}`),
  // Run one strategy on one stock
  analyzeStockOne: (ticker, strategy) =>
    api.get(`/legendary/stock/${ticker}/${strategy}`),
  // Consensus: stocks with ≥N strategies agreeing
  getConsensus: ({ minStrategies = 2, minScore = 50, lookbackDays = 300 } = {}) =>
    api.get('/legendary/consensus', { params: { min_strategies: minStrategies, min_score: minScore, lookback_days: lookbackDays } }),
  // Accuracy tracking
  getAccuracySummary: () =>
    api.get('/legendary/accuracy/summary'),
  getAccuracyHistory: ({ months = 12, strategy = null } = {}) =>
    api.get('/legendary/accuracy/history', { params: { months, strategy } }),
  getSignalHistory: ({ strategy, status, stopMode, stopProfile, days = 90, page = 1, pageSize = 50 } = {}) =>
    api.get('/legendary/accuracy/signals', {
      params: {
        strategy,
        status,
        stop_mode: stopMode,
        stop_profile: stopProfile,
        days,
        page,
        page_size: pageSize,
      },
    }),
  // Pre-Signal / Watchlist
  getWatchlist: ({ strategies = null, topN = 30, minScore = 55, lookbackDays = 500 } = {}) =>
    api.get('/legendary/watchlist', { params: { strategies, top_n: topN, min_score: minScore, lookback_days: lookbackDays } }),
  getWatchlistStrategy: (strategy, { topN = 30, minScore = 55 } = {}) =>
    api.get(`/legendary/watchlist/${strategy}`, { params: { top_n: topN, min_score: minScore } }),
};

// ── Concentration & Allocation API ──────────────────────────────────────────
export const concentrationAPI = {
  getDaily: (targetDate) =>
    api.get('/concentration/daily', { params: { target_date: targetDate } }),
  getContributors: (targetDate, topN = 15) =>
    api.get('/concentration/contributors', { params: { target_date: targetDate, top_n: topN } }),
  getBuckets: (targetDate) =>
    api.get('/concentration/buckets', { params: { target_date: targetDate } }),
  getAllocation: (targetDate) =>
    api.get('/concentration/allocation', { params: { target_date: targetDate } }),
  getReport: (targetDate) =>
    api.get('/concentration/report', { params: { target_date: targetDate } }),
  getHistory: (days = 30) =>
    api.get('/concentration/history', { params: { days } }),
  getTrafficLights: (targetDate) =>
    api.get('/concentration/traffic-lights', { params: { target_date: targetDate } }),
  calculate: (targetDate) =>
    api.post('/concentration/calculate', null, { params: { target_date: targetDate } }),
};

// ── Trend Predictions API ──────────────────────────────────────────
export const trendPredictionsAPI = {
  getPredictions: ({ targetDate, topN = 100, minScore = 0 } = {}) =>
    api.get('/trend/predictions', { params: { target_date: targetDate, top_n: topN, min_score: minScore } }),
  getTop: ({ n = 20, minScore = 55 } = {}) =>
    api.get('/trend/top', { params: { n, min_score: minScore } }),
  getRegime: () =>
    api.get('/trend/regime'),
  getDistribution: ({ targetDate } = {}) =>
    api.get('/trend/distribution', { params: { target_date: targetDate } }),
  getSymbol: (symbol, days = 30) =>
    api.get(`/trend/symbols/${symbol}`, { params: { days } }),
  getLivePrediction: (symbol) =>
    api.get(`/trend/predict/${symbol}`),

  // Accuracy tracking
  getAccuracySummary: (lastNDates = 30) =>
    api.get('/trend/accuracy/summary', { params: { last_n_dates: lastNDates } }),
  getDailyAccuracy: (lastNDates = 60) =>
    api.get('/trend/accuracy/daily', { params: { last_n_dates: lastNDates } }),
  getStockAccuracy: (lastNDates = 30, topN = 20) =>
    api.get('/trend/accuracy/stocks', { params: { last_n_dates: lastNDates, top_n: topN } }),
  getReturnProfile: (lastNDates = 60) =>
    api.get('/trend/accuracy/return-profile', { params: { last_n_dates: lastNDates } }),
  triggerEvaluation: () =>
    api.post('/trend/accuracy/evaluate'),
  calculate: (forceRetrain = false) =>
    api.post('/trend/calculate', null, { params: { force_retrain: forceRetrain }, timeout: 180000 }),
};

// ── ML Risk Intelligence API ──────────────────────────────────────
export const riskMLAPI = {
  getDashboard: (targetDate) =>
    api.get('/risk-ml/dashboard', { params: { target_date: targetDate } }),
  getHeatmap: (targetDate) =>
    api.get('/risk-ml/heatmap', { params: { target_date: targetDate } }),
  getStockDetail: (ticker, days = 60) =>
    api.get(`/risk-ml/stock/${ticker}`, { params: { days } }),
  getSeverity: (targetDate) =>
    api.get('/risk-ml/severity', { params: { target_date: targetDate } }),
  getTrend: (days = 30) =>
    api.get('/risk-ml/trend', { params: { days } }),
  getDates: (limit = 30) =>
    api.get('/risk-ml/dates', { params: { limit } }),
  getModel: () =>
    api.get('/risk-ml/model'),
  search: (q, targetDate) =>
    api.get('/risk-ml/search', { params: { q, target_date: targetDate } }),
  calculate: (calcDate, modelVersion = 'v9') =>
    api.post('/risk-ml/calculate', null, { params: { calc_date: calcDate, model_version: modelVersion }, timeout: 120000 }),
};

// ── ML Upside Intelligence API ──────────────────────────────────────
export const upsideMLAPI = {
  getDashboard: (targetDate) =>
    api.get('/upside-ml/dashboard', { params: { target_date: targetDate } }),
  getHeatmap: (targetDate) =>
    api.get('/upside-ml/heatmap', { params: { target_date: targetDate } }),
  getStockDetail: (ticker, days = 60) =>
    api.get(`/upside-ml/stock/${ticker}`, { params: { days } }),
  getTiers: (targetDate) =>
    api.get('/upside-ml/tiers', { params: { target_date: targetDate } }),
  getTrend: (days = 30) =>
    api.get('/upside-ml/trend', { params: { days } }),
  getDates: (limit = 30) =>
    api.get('/upside-ml/dates', { params: { limit } }),
  getModel: () =>
    api.get('/upside-ml/model'),
  search: (q, targetDate) =>
    api.get('/upside-ml/search', { params: { q, target_date: targetDate } }),
  calculate: (calcDate, modelVersion = 'u1') =>
    api.post('/upside-ml/calculate', null, { params: { calc_date: calcDate, model_version: modelVersion }, timeout: 120000 }),
};

// ── Pattern Cluster Analysis API ──────────────────────────────────────
export const patternClusterAPI = {
  scan: (scanDate, scanDays = 20, nClusters = 12, topK = 3) =>
    api.get('/pattern-cluster/scan', { params: { scan_date: scanDate, scan_days: scanDays, n_clusters: nClusters, top_k: topK } }),
  getLeaderboard: (scanDate, lookbackDays = 90) =>
    api.get('/pattern-cluster/leaderboard', { params: { scan_date: scanDate, lookback_days: lookbackDays } }),
  getDelayed: (scanDate, topK = 3, maxDist = 3) =>
    api.get('/pattern-cluster/delayed', { params: { scan_date: scanDate, top_k: topK, max_dist: maxDist } }),
  getRotation: (scanDate, topK = 3) =>
    api.get('/pattern-cluster/rotation', { params: { scan_date: scanDate, top_k: topK } }),
  runBacktest: (params) =>
    api.post('/pattern-cluster/backtest', params),
};

// Market Risk Unified API - Unified Early Warning System
export const marketRiskUnifiedAPI = {
  getLatest: () => api.get('/market-risk/latest'),
  getHistory: (days = 30) => api.get('/market-risk/history', { params: { days } }),
  getByDate: (date) => api.get(`/market-risk/date/${date}`),
  getAlerts: (days = 60) => api.get('/market-risk/alerts', { params: { days } }),
  getVnindexCorrelation: (days = 90) => api.get('/market-risk/vnindex-correlation', { params: { days } }),
  calculate: (calcDate) => api.post('/market-risk/calculate', null, { params: calcDate ? { calc_date: calcDate } : {} }),
};

// ── VSA Scanner API ──────────────────────────────────────
export const vsaAPI = {
  scanSync: (daysBack = 60) =>
    api.post('/vsa/scan/run-sync', null, { params: { days_back: daysBack } }),
  scanTrigger: (daysBack = 60, gradeFilter = null) =>
    api.post('/vsa/scan/trigger', null, { params: { days_back: daysBack, grade_filter: gradeFilter } }),
  getLatest: (topN = 50, executionGrade = null, minPriorityScore = 0, setupTypes = null, sectors = null, targetDate = null) =>
    api.get('/vsa/scan/latest', { 
      params: { 
        top_n: topN, 
        execution_grade: executionGrade,
        min_priority_score: minPriorityScore,
        setup_types: setupTypes,
        sectors: sectors,
        target_date: targetDate,
      } 
    }),
  getSummary: () =>
    api.get('/vsa/summary/latest'),
  getMarketRegime: () =>
    api.get('/vsa/market-regime'),
  getRegimeHistory: (days = 60) =>
    api.get('/vsa/regime/history', { params: { days } }),
  getTickerSignals: (ticker, limit = 30) =>
    api.get(`/vsa/signals/${ticker}`, { params: { limit } }),
  getTickerDetail: (ticker, days = 20) =>
    api.get(`/vsa/ticker/${ticker}/detail`, { params: { days } }),
  getSectorsRanking: () =>
    api.get('/vsa/sectors/ranking'),
  getSetupDistribution: () =>
    api.get('/vsa/stats/setup-distribution'),
  getSignalsByDate: (targetDate, executionGrade = null) =>
    api.get('/vsa/signals/date', { params: { target_date: targetDate, execution_grade: executionGrade } }),
  getWatchlistImmediate: () =>
    api.get('/vsa/watchlists/immediate'),
  getWatchlistTierA: () =>
    api.get('/vsa/watchlists/tier-a'),
  getWatchlistAvoid: () =>
    api.get('/vsa/watchlists/avoid'),
  getRecommendations: () =>
    api.get('/vsa/watchlists/recommendations'),
  getSignalHistory: (days = 30, grade = null) =>
    api.get('/vsa/history/signals', { params: { days, grade } }),
};

// ── Stock Risk Composite API ──────────────────────────────────────
export const riskCompositeAPI = {
  getOverview: (targetDate) =>
    api.get('/risk-composite/overview', { params: { target_date: targetDate } }),
  getStocks: (targetDate, riskLabel, sort = 'risk_desc', limit = 200) =>
    api.get('/risk-composite/stocks', { params: { target_date: targetDate, risk_label: riskLabel, sort, limit } }),
  getStockDetail: (ticker, targetDate) =>
    api.get(`/risk-composite/stock/${ticker}`, { params: { target_date: targetDate } }),
  getStockHistory: (ticker, days = 60) =>
    api.get(`/risk-composite/stock/${ticker}/history`, { params: { days } }),
  getExtreme: (targetDate) =>
    api.get('/risk-composite/extreme', { params: { target_date: targetDate } }),
  getFlags: (targetDate, minFlags = 1) =>
    api.get('/risk-composite/flags', { params: { target_date: targetDate, min_flags: minFlags } }),
  compare: (tickers, targetDate) =>
    api.get('/risk-composite/compare', { params: { tickers, target_date: targetDate } }),
  calculate: (calcDate, skipShap = false) =>
    api.post('/risk-composite/calculate', null, { params: { calc_date: calcDate, skip_shap: skipShap }, timeout: 120000 }),
};

// ── Niche Regime API ──────────────────────────────────────────
export const nicheRegimeAPI = {
  getCurrent: (targetDate) =>
    api.get('/niche-regime/current', { params: targetDate ? { target_date: targetDate } : {} }),
  getCandidates: (targetDate, limit = 30) =>
    api.get('/niche-regime/candidates', { params: { target_date: targetDate, limit } }),
  getHistory: (days = 30) =>
    api.get('/niche-regime/history', { params: { days } }),
  getSummary: () =>
    api.get('/niche-regime/summary'),
  calculate: (targetDate) =>
    api.post('/niche-regime/calculate', null, { params: targetDate ? { target_date: targetDate } : {} }),
};

// ── Detector Paper Trading API ────────────────────────────────
export const detectorPaperTradingAPI = {
  getEquityCurve: (days = 90) =>
    api.get('/paper-trading/equity-curve', { params: { days } }),
  getStats: (days = 90) =>
    api.get('/paper-trading/stats', { params: { days } }),
  getPositions: () =>
    api.get('/paper-trading/positions'),
  getTrades: (limit = 50) =>
    api.get('/paper-trading/trades', { params: { limit } }),
  getMonthly: () =>
    api.get('/paper-trading/monthly'),
  backfill: (startDate, endDate) =>
    api.post('/paper-trading/backfill', null, {
      params: { start_date: startDate, end_date: endDate },
      timeout: 120000,
    }),
  processDay: (tradeDate) =>
    api.post('/paper-trading/process-day', null, {
      params: tradeDate ? { trade_date: tradeDate } : {},
    }),
};

// ── Detector Signals API ──────────────────────────────────────
export const detectorSignalsAPI = {
  getLatest: () =>
    api.get('/detector-signals/latest'),
  getOutcomes: (days = 90) =>
    api.get('/detector-signals/outcomes', { params: { stats_days: days } }),
};

// ── Surge Readiness API ──────────────────────────────────────
export const surgeReadinessAPI = {
  getLatest: (params = {}) =>
    api.get('/surge-readiness/latest', { params }),
  getHistory: (days = 10) =>
    api.get('/surge-readiness/history', { params: { days } }),
  getJournal: (params = {}) =>
    api.get('/surge-readiness/journal', { params }),
  getOutcomes: (days = 90) =>
    api.get('/surge-readiness/outcomes', { params: { days } }),
  getCalibration: (statsDays = 180, bins = 6) =>
    api.get('/surge-readiness/calibration', { params: { stats_days: statsDays, bins } }),
  getTicker: (ticker, days = 90) =>
    api.get(`/surge-readiness/ticker/${ticker}`, { params: { days } }),
  calculate: (targetDate = null, limit = 50, minScore = null) =>
    api.post('/surge-readiness/calculate', null, {
      params: {
        target_date: targetDate,
        limit,
        min_score: minScore,
      },
    }),
  snapshot: (targetDate = null, minScore = null) =>
    api.post('/surge-readiness/snapshot', null, {
      params: {
        target_date: targetDate,
        min_score: minScore,
      },
    }),
  trackOutcomes: (asOfDate = null) =>
    api.post('/surge-readiness/track-outcomes', null, {
      params: asOfDate ? { as_of_date: asOfDate } : {},
    }),
  backtest: (payload) =>
    api.post('/surge-readiness/backtest', payload),
};

// ── Stock Recommendations API ─────────────────────────────────
export const stockRecommendationsAPI = {
  getRecommendations: (signalDate) =>
    api.get('/recommendations/detector', {
      params: signalDate ? { signal_date: signalDate } : {},
    }),
  getPicks: (signalDate) =>
    api.get('/recommendations/detector/picks', {
      params: signalDate ? { signal_date: signalDate } : {},
    }),
  getHolding: () =>
    api.get('/recommendations/detector/holding'),
};

// ── Stock Strength Scanner API ──────────────────────────
export const stockStrengthAPI = {
  getScores: (params) =>
    api.get('/stock-strength/scores', { params }),
  getLeaders: (params) =>
    api.get('/stock-strength/leaders', { params }),
  getTicker: (ticker, days) =>
    api.get(`/stock-strength/ticker/${ticker}`, { params: { days } }),
  getSummary: (tradeDate) =>
    api.get('/stock-strength/summary', {
      params: tradeDate ? { trade_date: tradeDate } : {},
    }),
  calculate: () =>
    api.post('/stock-strength/calculate'),
  getActionBoard: (tradeDate) =>
    api.get('/stock-strength/action-board', {
      params: tradeDate ? { trade_date: tradeDate } : {},
    }),
};

export const ichimokuTransitionAPI = {
  getScan: (params = {}) =>
    api.get('/ichimoku-transition/scan', { params }),
  getLiveScan: (params = {}) =>
    api.get('/ichimoku-transition/scan/live', { params }),
  getTopUpside: (params = {}) =>
    api.get('/ichimoku-transition/scan/top-upside', { params }),
  getLiveTopUpside: (params = {}) =>
    api.get('/ichimoku-transition/scan/top-upside/live', { params }),
  getTicker: (ticker, days = 30) =>
    api.get(`/ichimoku-transition/ticker/${ticker}`, { params: { days } }),
  calculate: (tradeDate) =>
    api.get('/ichimoku-transition/calculate', {
      params: tradeDate ? { trade_date: tradeDate } : {},
    }),
  backtest: (params = {}) =>
    api.get('/ichimoku-transition/backtest', { params }),
};

// ── ICB Industry Classification API ──────────────────────────
export const icbAPI = {
  getRankings: (level = 2, topN = undefined, asOfDate = undefined) =>
    api.get('/icb/rankings', {
      params: { level, top_n: topN, as_of_date: asOfDate },
    }),
  getHierarchy: (level) =>
    api.get('/icb/hierarchy', { params: level ? { level } : {} }),
  getStock: (ticker) =>
    api.get(`/icb/stock/${ticker}`),
  getPeers: (ticker, level = 2) =>
    api.get(`/icb/peers/${ticker}`, { params: { level } }),
  getSaturation: (level = 2, asOfDate = undefined) =>
    api.get('/icb/saturation', {
      params: { level, as_of_date: asOfDate },
    }),
  getStocksInGroup: (icbCode) =>
    api.get(`/icb/${icbCode}/stocks`),
  getLeaders: (icbCode, limit = 30) =>
    api.get(`/icb/${icbCode}/leaders`, { params: { limit } }),
  getOpportunityMap: (level = 2) =>
    api.get('/icb/opportunity-map', { params: { level } }),
  calculate: (asOfDate) =>
    api.post('/icb/calculate', null, { params: asOfDate ? { as_of_date: asOfDate } : {} }),
};

// ── Behavior Labels API ──────────────────────────────────────
export const behaviorAPI = {
  getScores: (params = {}) =>
    api.get('/behavior/scores', { params }).then(res => res.data),
  getSummary: (tradeDate) =>
    api.get('/behavior/summary', { params: { trade_date: tradeDate } }).then(res => res.data),
  getWatchlist: (params = {}) =>
    api.get('/behavior/watchlist', { params }).then(res => res.data),
  getTickerHistory: (ticker, days = 30) =>
    api.get(`/behavior/ticker/${ticker}`, { params: { days } }).then(res => res.data),
  calculate: (tradeDate) =>
    api.post('/behavior/calculate', null, { params: { trade_date: tradeDate } }),
};

// ── Darvas Box API ───────────────────────────────────────────
export const darvasAPI = {
  getScores: (params = {}) =>
    api.get('/darvas-boxes/scores', { params }).then(res => res.data),
  getSummary: (tradeDate) =>
    api.get('/darvas-boxes/summary', { params: { trade_date: tradeDate } }).then(res => res.data),
  getLeaders: (topN = 20) =>
    api.get('/darvas-boxes/leaders', { params: { top_n: topN } }).then(res => res.data),
  getBreakoutWatch: (topN = 20) =>
    api.get('/darvas-boxes/breakout-watch', { params: { top_n: topN } }).then(res => res.data),
  getAvoid: (topN = 30) =>
    api.get('/darvas-boxes/avoid', { params: { top_n: topN } }).then(res => res.data),
  getTicker: (ticker) =>
    api.get(`/darvas-boxes/ticker/${ticker}`).then(res => res.data),
};

// ── Statistical Risk API (GARCH / Markowitz / VaR / Calibration) ──
export const statisticalAPI = {
  // GARCH
  getGarch: (ticker) =>
    api.get(`/safe-invest/garch/${ticker}`).then(res => res.data),
  getGarchBatch: (tickers) =>
    api.post('/safe-invest/garch/batch', { tickers }).then(res => res.data),
  getMarketRegime: () =>
    api.get('/safe-invest/garch/market-regime').then(res => res.data),
  getGarchStopLoss: (ticker, entryPrice, multiplier = 2) =>
    api.post('/safe-invest/garch/stop-loss', { ticker, entry_price: entryPrice, multiplier }).then(res => res.data),
  // VaR / ES
  getVaR: (ticker) =>
    api.get(`/safe-invest/var/${ticker}`).then(res => res.data),
  getPortfolioVaR: (positions) =>
    api.post('/safe-invest/var/portfolio', { positions }).then(res => res.data),
  // Kelly
  getKelly: (ticker) =>
    api.get(`/safe-invest/kelly/${ticker}`).then(res => res.data),
  getKellyPosition: (data) =>
    api.post('/safe-invest/kelly/position-size', data).then(res => res.data),
  // Markowitz
  optimizePortfolio: (tickers, method = 'tangency', lookback = 252, maxWeight = 0.15) =>
    api.post('/portfolio/optimize', { tickers, method, lookback_days: lookback, max_weight: maxWeight }).then(res => res.data),
  getEfficientFrontier: (tickers, nPoints = 10, lookback = 252, maxWeight = 0.15) =>
    api.post('/portfolio/efficient-frontier', { tickers, n_points: nPoints, lookback_days: lookback, max_weight: maxWeight }).then(res => res.data),
  // Calibration
  getCalibrationOverview: () =>
    api.get('/calibrate/overview').then(res => res.data),
  calibrateSingle: (score) =>
    api.get('/calibrate/single', { params: { score } }).then(res => res.data),
};

export const behaviorStatsAPI = {
  // Latest signal (today's panic/fomo)
  getLatestSignal: () => api.get('/behavior/signal/latest').then(res => res.data),
  
  // Signal for specific date
  getSignalForDate: (date) => api.get(`/behavior/signal/${date}`).then(res => res.data),
  
  // Signal range (default 30 days)
  getSignalRange: (startDate = null, endDate = null, days = 30) =>
    api.get('/behavior/signals', { params: { start_date: startDate, end_date: endDate, days } }).then(res => res.data),
  
  // Event statistics (win rate, returns, etc)
  getEventStatistics: (eventName, horizonDays = 20) =>
    api.get(`/behavior/events/${eventName}`, { params: { horizon_days: horizonDays } }).then(res => res.data),
  
  // Similar historical days (KNN neighbors)
  getSimilarDays: (date, limit = 30) =>
    api.get(`/behavior/similar-days/${date}`, { params: { limit } }).then(res => res.data),
  
  // Market insights (interpretation, regime, recommendation)
  getMarketInsights: () => api.get('/behavior/insights').then(res => res.data),
  
  // VNINDEX correlation with panic/fomo
  getVNIndexCorrelation: (startDate = null, endDate = null, limit = 2000, lookbackWindow = 10) =>
    api.get('/behavior/vnindex-correlation', { 
      params: { start_date: startDate, end_date: endDate, limit, lookback_window: lookbackWindow } 
    }).then(res => res.data),

  // Recalculate panic/fomo scores (on-demand)
  calculate: (params = {}) =>
    api.post('/behavior/stats/calculate', null, { params, timeout: 300000 }).then(res => res.data),
};

export default api;

